import { ERROR_CODES } from 'api/error-handling/error-codes';
import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { AthleteAppointment, AthleteAppointmentWrite, ErrorMessage } from 'types';
import { client } from 'utils/api-client';

export default function useUpdateAthleteAppointment(): UseMutationResult<
  AxiosResponse<AthleteAppointment>,
  AxiosError<ErrorMessage>,
  AthleteAppointmentWrite
> {
  const queryClient = useQueryClient();

  const updateAthleteAppointment = (
    athleteAppointment: AthleteAppointmentWrite,
  ): Promise<AxiosResponse<AthleteAppointment>> => {
    return client.put(
      `athlete-appointments/${athleteAppointment.athleteAppointmentId}/`,
      athleteAppointment,
    );
  };

  const invalidateAthleteAppointmentQueries = (): void => {
    queryClient.invalidateQueries({
      queryKey: ['athleteEvents'],
    });
    queryClient.invalidateQueries({
      queryKey: ['athleteAppointments'],
    });
    queryClient.invalidateQueries({
      queryKey: ['appointments'],
    });
    queryClient.invalidateQueries({
      queryKey: 'events',
    });
    queryClient.invalidateQueries({
      queryKey: ['totalAttended'],
    });
    queryClient.invalidateQueries({
      queryKey: ['totalReserved'],
    });
  };

  return useMutation(updateAthleteAppointment, {
    onSuccess: () => {
      invalidateAthleteAppointmentQueries();
    },
    onError: (error: AxiosError<ErrorMessage>) => {
      // If it's a no-op error (NO_STATE_CHANGE), invalidate cache to ensure data is refreshed
      const errorCode = error.response?.data?.errorCode;
      if (errorCode === ERROR_CODES.COMMON.NO_STATE_CHANGE) {
        invalidateAthleteAppointmentQueries();
      }
    },
  });
}
